import { useEffect, useRef } from 'react';
import { DEFAULT_PAGE_SIZE } from '../util';

/**
 * Custom hook to expose external API for AB testing team
 * Allows programmatic manipulation of VWA filters
 *
 * @param {Object} filters - Current filter state
 * @param {Function} updateURL - Function to update URL and apply filters
 * @param {Function} setPageIndex - Function to reset page index
 * @param {Function} setQuickFilterActive - Function to deactivate quick filters
 * @param {Function} setPresetRange - Function to reset preset price range
 */
const useVWAExternalAPI = ({ filters, updateURL, setPageIndex, setQuickFilterActive, setPresetRange }) => {
    // Use refs to maintain stable function references and avoid recreating the API on every render
    const updateURLRef = useRef(updateURL);
    const setPageIndexRef = useRef(setPageIndex);
    const setQuickFilterActiveRef = useRef(setQuickFilterActive);
    const setPresetRangeRef = useRef(setPresetRange);
    const filtersRef = useRef(filters);

    // Update refs when dependencies change
    useEffect(() => {
        updateURLRef.current = updateURL;
        setPageIndexRef.current = setPageIndex;
        setQuickFilterActiveRef.current = setQuickFilterActive;
        setPresetRangeRef.current = setPresetRange;
        filtersRef.current = filters;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Warn if window.hpVwaAPI already exists
        if (window.hpVwaAPI) {
            console.warn('VWA API: window.hpVwaAPI already exists and will be overwritten.');
        }

        /**
         * External API for updating VWA filters programmatically
         * @param {Object} queryObject - Filter object in format: { facetKey: { value1: value1, value2: value2 }, ... }
         *                               Example: { brand: { 'HP': 'HP' }, Price: { '0-600': '0-600' } }
         * @param {boolean} replaceFilters - If true, replaces all current filters. If false, merges with existing filters. Default: true
         * @returns {Object} Updated filter state
         */
        const updateVWAFilters = (queryObject, replaceFilters = true) => {
            try {
                if (!queryObject || typeof queryObject !== 'object') {
                    console.error('VWA API: Invalid query object. Expected object format.');
                    return filtersRef.current;
                }

                let updatedFilters;

                if (replaceFilters) {
                    // Replace all current filters with new ones
                    updatedFilters = { ...queryObject };
                } else {
                    // Merge new filters with existing filters
                    updatedFilters = { ...filtersRef.current };
                    Object.keys(queryObject).forEach(facetKey => {
                        if (queryObject[facetKey] && typeof queryObject[facetKey] === 'object') {
                            updatedFilters[facetKey] = {
                                ...(updatedFilters[facetKey] || {}),
                                ...queryObject[facetKey],
                            };
                        } else {
                            updatedFilters[facetKey] = queryObject[facetKey];
                        }
                    });
                }

                // Apply the updated filters
                updateURLRef.current(updatedFilters);
                setPageIndexRef.current(DEFAULT_PAGE_SIZE);
                setQuickFilterActiveRef.current(-1);
                setPresetRangeRef.current([]);

                return updatedFilters;
            } catch (error) {
                console.error('VWA API: Error updating filters', error);
                return filtersRef.current;
            }
        };

        // Expose API on window object
        window.hpVwaAPI = {
            updateFilters: updateVWAFilters,
            getCurrentFilters: () => ({ ...filtersRef.current }),
            clearFilters: () => {
                updateURLRef.current({});
                setPresetRangeRef.current([]);
                setQuickFilterActiveRef.current(-1);
                setPageIndexRef.current(DEFAULT_PAGE_SIZE);
                return {};
            },
        };

        // Cleanup on unmount - only delete if it's still our API object
        return () => {
            if (window.hpVwaAPI && window.hpVwaAPI.updateFilters === updateVWAFilters) {
                delete window.hpVwaAPI;
            }
        };
    }, []); // Empty dependency array - API is created once and uses refs for current values
};

export default useVWAExternalAPI;
